import { useEffect, useMemo, useState } from 'react';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import { useLanguage } from '../../../contexts/language';
import {
  DAYJS_VISUALIZATION_DATE_FORMAT,
  DAYJS_VISUALIZATION_TIME_FORMAT,
} from '../../../utils/date';
import {
  Autocomplete,
  Button,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { SelectItem } from '../../../types/core';
import useAnalyticsData, {
  GlobalFilterType,
  PassengersFilter,
} from './useAnalyticsData';
import AnalyticsCard from './AnalyticsCard';
import {
  PassengersGroupedDataItem,
  CargoGroupedDataItem,
  GroupedDataItem,
  GroupedDataItemDefinitionsMap,
  GroupedDataItemOrderArray,
  FuelGroupedDataItem,
  JourneysGroupedDataItem,
} from './types';
import CsvExportDialog from './CsvExportDialog';
import { useCurrentUser } from '../../../contexts/currentUser';
import { Role } from '../../../types/user';
import { useMutation } from '@tanstack/react-query';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useSnackbar } from 'notistack';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';
import { RiRefreshLine } from '@remixicon/react';

interface AnalyticsCardInfo<T extends GroupedDataItem> {
  title: string;
  fieldDefinitions: GroupedDataItemDefinitionsMap<T>;
  fieldsOrder: GroupedDataItemOrderArray<T>;
}

const AnalyticsPage = () => {
  const { hasRole } = useCurrentUser();
  const isAdmin = hasRole(Role.ADMIN);
  const { t } = useLanguage();
  const [isDisplayingCsvExportDialog, setIsDisplayingCsvExportDialog] =
    useState(false);
  const {
    filterItems,
    filterSelectedItems,
    globalFilter,
    passengersData,
    cargoData,
    fuelData,
    journeysData,
    isFetchingAnalytics,
    lastUpdate,
    allowedYears: fetchedAllowedYears,
    passengersFilter,
    setGlobalFilter,
    setPassengersFilter,
    refetch,
  } = useAnalyticsData();

  const { requestAnalyticsRecalculation } = useIotsApi();
  const analyticsUpdateMutation = useMutation({
    mutationFn: () => requestAnalyticsRecalculation(),
  });
  const getErrorMessage = useApiErrorMessageGetter();

  const [allowedYears, setAllowedYears] = useState(() =>
    globalFilter.year ? [globalFilter.year.toString()] : [],
  );

  useEffect(() => {
    // Allowed years are accumulative as the fetched ones could change dependeing on the analytics object retrieved
    setAllowedYears(prev =>
      [...new Set([...prev, ...fetchedAllowedYears])].sort((a, b) =>
        b.localeCompare(a),
      ),
    );
  }, [fetchedAllowedYears]);

  const {
    monthFormatter,
    journeysAnalyticsCardInfo,
    passengersAnalyticsCardInfo,
    cargoAnalyticsCardInfo,
    fuelAnalyticsCardInfo,
  } = useMemo(() => {
    const monthFormatter = (month: string, reduced = false) => {
      const monthLabel = t(`months.${month}`);
      if (reduced) {
        return monthLabel.slice(0, 3);
      }
      return monthLabel;
    };
    const journeysAnalyticsCardInfo: AnalyticsCardInfo<JourneysGroupedDataItem> =
      {
        title: t('content.journeys'),
        fieldDefinitions: {
          asScheduledJourneys: {
            color: '#1ed88e',
            label: t('journey-status.asscheduled'),
          },
          cancelledJourneys: {
            color: '#e28e97',
            label: t('journey-status.cancelled'),
          },
          reliefEventJourneys: {
            color: '#1ea7d8',
            label: t('journey-status.reliefevent'),
          },
          rescheduledJourneys: {
            color: '#ffd91b',
            label: t('journey-status.rescheduled'),
          },
        },
        fieldsOrder: [
          'reliefEventJourneys',
          'asScheduledJourneys',
          'rescheduledJourneys',
          'cancelledJourneys',
        ],
      };
    const passengersAnalyticsCardInfo: AnalyticsCardInfo<PassengersGroupedDataItem> =
      {
        title: t('content.passengers'),
        fieldDefinitions: {
          adult: {
            color: '#1ed88e',
            label: t('content.adults'),
          },
          underEighteen: {
            color: '#8ed6e2',
            label: t('content.under-18'),
          },
          underFive: {
            color: '#e28e97',
            label: t('content.under-5'),
          },
          students: {
            color: '#ffd91b',
            label: t('content.students'),
          },
          overSixty: {
            color: '#1ea7d8',
            label: t('content.over-65'),
          },
        },
        fieldsOrder: [
          'overSixty',
          'adult',
          'students',
          'underEighteen',
          'underFive',
        ],
      };
    const cargoAnalyticsCardInfo: AnalyticsCardInfo<CargoGroupedDataItem> = {
      title: t('content.cargo-hauled'),
      fieldDefinitions: {
        cargoHauled: {
          color: '#8ed6e2',
          label: t('content.cargo-hauled'),
        },
      },
      fieldsOrder: ['cargoHauled'],
    };
    const fuelAnalyticsCardInfo: AnalyticsCardInfo<FuelGroupedDataItem> = {
      title: t('content.fuel-usage'),
      fieldDefinitions: {
        fuelUsage: {
          color: '#8ed6e2',
          label: t('content.fuel-usage'),
        },
      },
      fieldsOrder: ['fuelUsage'],
    };

    return {
      monthFormatter,
      journeysAnalyticsCardInfo,
      passengersAnalyticsCardInfo,
      cargoAnalyticsCardInfo,
      fuelAnalyticsCardInfo,
    };
  }, [t]);

  const { enqueueSnackbar } = useSnackbar();

  return (
    <MainSectionTemplate
      title={t('content.analytics')}
      subtitle={`${t('content.last-update')}: ${lastUpdate ? lastUpdate.format(`${DAYJS_VISUALIZATION_DATE_FORMAT} ${DAYJS_VISUALIZATION_TIME_FORMAT}`) : ''}`}
      isLoading={isFetchingAnalytics}
      actions={
        <div className="flex items-center gap-4">
          {isAdmin && (
            <Tooltip title={t('content.refresh-analytics-description')}>
              <Button
                variant="outlined"
                onClick={() => {
                  analyticsUpdateMutation.mutate(undefined, {
                    onSuccess: () => {
                      enqueueSnackbar({
                        variant: 'success',
                        message: t('content.refresh-analytics-request-success'),
                      });
                    },
                    onError: error => {
                      enqueueSnackbar({
                        variant: 'error',
                        message: getErrorMessage(
                          error,
                          t('content.refresh-analytics-request-error'),
                        ),
                      });
                      analyticsUpdateMutation.reset();
                    },
                    onSettled: () => {
                      refetch();
                    },
                  });
                }}
                startIcon={
                  <SvgIcon
                    className={
                      analyticsUpdateMutation.isPending
                        ? 'animate-spin'
                        : undefined
                    }
                    component={RiRefreshLine}
                  />
                }
                disabled={analyticsUpdateMutation.isPending}
              >
                {t('content.refresh')}
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            onClick={() => setIsDisplayingCsvExportDialog(true)}
          >
            {t('content.export-csv')}
          </Button>
          <CsvExportDialog
            allowedOrderedYears={allowedYears}
            isOpen={isDisplayingCsvExportDialog}
            onClose={() => {
              setIsDisplayingCsvExportDialog(false);
            }}
          />
        </div>
      }
      afterTitleContent={
        <div className="grid grid-cols-[1fr] grid-rows-[repeat(3,auto)] md:grid-cols-[min-content_1fr_min-content] md:grid-rows-[auto] gap-4">
          <ToggleButtonGroup
            value={globalFilter.type}
            exclusive
            onChange={(_, newValue: typeof globalFilter.type) => {
              setGlobalFilter(prev => ({
                ...prev,
                type: newValue,
                values: [],
              }));
            }}
          >
            <ToggleButton
              className="whitespace-nowrap"
              value={GlobalFilterType.Islands}
            >
              {t('content.by-field', { FIELD: t('content.island') })}
            </ToggleButton>
            <ToggleButton
              className="whitespace-nowrap"
              value={GlobalFilterType.Contracts}
            >
              {t('content.by-field', { FIELD: t('content.contract') })}
            </ToggleButton>
            <ToggleButton
              className="whitespace-nowrap"
              value={GlobalFilterType.Counties}
            >
              {t('content.by-field', { FIELD: t('content.county') })}
            </ToggleButton>
          </ToggleButtonGroup>
          <Autocomplete<SelectItem, true>
            multiple
            value={filterSelectedItems}
            options={filterItems}
            onChange={(_event, selectedOptions) => {
              setGlobalFilter(prev => ({
                ...prev,
                values: selectedOptions.map(({ value }) => value),
              }));
            }}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t(`content.${globalFilter.type}`)}
              />
            )}
            getOptionLabel={island => island.label}
          />
          <Autocomplete<string, false, true>
            value={globalFilter.year.toString()}
            className="w-[100px]"
            options={allowedYears}
            multiple={false}
            disableClearable
            onChange={(_event, option) =>
              option &&
              setGlobalFilter(prev => ({
                ...prev,
                year: Number(option),
              }))
            }
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t(`content.${globalFilter.type}`)}
              />
            )}
          />
        </div>
      }
    >
      <div className="grid grid-rows-4 md:grid-rows-3 md:grid-cols-2 gap-4">
        <AnalyticsCard
          {...journeysAnalyticsCardInfo}
          fullWidth
          yearlyData={journeysData}
          monthFormatter={monthFormatter}
        />
        <AnalyticsCard
          {...fuelAnalyticsCardInfo}
          yearlyData={fuelData}
          monthFormatter={monthFormatter}
        />
        <AnalyticsCard
          {...cargoAnalyticsCardInfo}
          yearlyData={cargoData}
          monthFormatter={monthFormatter}
        />
        <AnalyticsCard
          {...passengersAnalyticsCardInfo}
          fullWidth
          yearlyData={passengersData}
          monthFormatter={monthFormatter}
          headerActions={
            <Select
              value={passengersFilter}
              onChange={event =>
                setPassengersFilter(event.target.value as PassengersFilter)
              }
            >
              <MenuItem value={PassengersFilter.All}>
                {t(`content.all`)}
              </MenuItem>
              <MenuItem value={PassengersFilter.Islanders}>
                {t(`content.islanders`)}
              </MenuItem>
              <MenuItem value={PassengersFilter.NonIslanders}>
                {t(`content.non-islanders`)}
              </MenuItem>
            </Select>
          }
        />
      </div>
    </MainSectionTemplate>
  );
};

export default AnalyticsPage;
